import React from "react";
import { Star, ExternalLink, X, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, formatGBP, logEvent } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const labelStyles = {
  "Best match": "bg-brand-green text-brand-cream",
  Experience: "bg-brand-burgundy text-brand-cream",
  "Premium pick": "bg-brand-amber text-white",
  "Great practical choice": "bg-white text-brand-green border border-brand-green/30",
  "Personal touch": "bg-white text-brand-burgundy border border-brand-burgundy/30",
  "Something different": "bg-brand-cream2 text-brand-ink",
};

export default function GiftCard({ rec, index, sessionId, recId, onUnsuitable, showWhy = true }) {
  const p = rec.product;
  const label = rec.label || "Great choice";
  const wishlist = useWishlist();
  const { user, setAuthOpen } = useAuth();
  const saved = wishlist?.has(p.product_id);
  const outOfStock = p.in_stock === false;
  const slug = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const sameSource = slug(p.brand) === slug(p.retailer);
  const affiliated = p.source === "awin" ||
    (!!p.affiliate_url && p.affiliate_active !== false && p.affiliate_provider && p.affiliate_provider !== "none");

  const trackUrl = `${API}/track/redirect?product_id=${p.product_id}&session_id=${encodeURIComponent(sessionId || "")}` +
    (recId ? `&rec_id=${encodeURIComponent(recId)}` : "");

  const viewGift = () => {
    logEvent("recommendation_click", { session_id: sessionId, product_id: p.product_id });
  };

  const toggleHeart = () => {
    const adding = wishlist?.toggle(p.product_id);
    if (adding && !user) {
      toast("Saved to your wishlist", {
        description: "Sign in to keep it across devices.",
        action: { label: "Sign in", onClick: () => setAuthOpen(true) },
      });
    } else if (adding) {
      toast.success("Added to wishlist");
    }
  };

  return (
    <article
      data-testid={`gift-card-${index}`}
      className="group relative flex flex-col bg-white rounded-2xl border border-brand-cream2 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 gm-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-brand-cream2">
        <img src={p.image} alt={p.name} loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${outOfStock ? "grayscale opacity-70" : ""}`} />
        <span className={`absolute top-3 left-3 text-[11px] font-mono uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full shadow-sm ${labelStyles[label] || "bg-brand-cream2 text-brand-ink"}`}>
          {label}
        </span>
        {rec.match != null && (
          <span className="absolute top-3 right-3 text-[11px] font-mono font-bold px-2 py-1 rounded-full bg-white/90 text-brand-green backdrop-blur">
            {rec.match}%
          </span>
        )}
        {wishlist && (
          <button onClick={toggleHeart} data-testid={`gift-card-heart-${index}`}
            title={saved ? "Remove from wishlist" : "Save to wishlist"}
            className="absolute bottom-3 left-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors shadow-sm">
            <Heart className={`w-4 h-4 transition-colors ${saved ? "fill-brand-burgundy text-brand-burgundy" : "text-brand-ink/70"}`} />
          </button>
        )}
        {outOfStock && (
          <span className="absolute bottom-3 right-3 text-[11px] font-mono uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-brand-ink/85 text-brand-cream">
            Out of stock
          </span>
        )}
        {onUnsuitable && !outOfStock && (
          <button onClick={() => onUnsuitable(p.product_id)} data-testid={`gift-card-unsuitable-${index}`}
            title="Not suitable"
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-brand-burgundy hover:text-white flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-serif text-lg leading-snug text-brand-ink">{p.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{sameSource ? p.retailer : `${p.brand} · ${p.retailer}`}</p>
          </div>
          <span className="font-mono font-bold text-brand-green whitespace-nowrap">{formatGBP(p.price)}</span>
        </div>

        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
          {p.rating ? (
            <>
              <Star className="w-3.5 h-3.5 fill-brand-amber text-brand-amber" />
              <span className="font-medium text-brand-ink">{p.rating}</span>
              {p.reviews ? <span>({p.reviews.toLocaleString()} reviews)</span> : null}
            </>
          ) : (
            <span data-testid={`gift-card-live-price-${index}`}>Live price from {p.retailer}</span>
          )}
        </div>

        {showWhy && rec.why && (
          <div className="mt-3 p-3 rounded-xl bg-brand-cream2/60 border border-brand-cream2">
            <p className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-brand-amber font-semibold mb-1">
              <Sparkles className="w-3 h-3" /> Why we picked it
            </p>
            <p className="text-sm text-brand-ink/80 leading-relaxed">{rec.why}</p>
          </div>
        )}

        {outOfStock ? (
          <Button disabled data-testid={`gift-card-view-button-${index}`}
            className="mt-4 w-full bg-brand-green rounded-full gap-2 disabled:opacity-50">
            Currently unavailable
          </Button>
        ) : (
          <Button asChild onClick={viewGift} data-testid={`gift-card-view-button-${index}`}
            className="mt-4 w-full bg-brand-green hover:bg-brand-greendark rounded-full gap-2 group/btn">
            <a href={trackUrl} target="_blank" rel="sponsored nofollow noopener noreferrer">
              View at {p.retailer} <ExternalLink className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" />
            </a>
          </Button>
        )}
        {affiliated && (
          <p className="mt-2 text-[11px] text-muted-foreground text-center" data-testid={`gift-card-affiliate-note-${index}`}>
            Affiliate link — we may earn a commission
          </p>
        )}
      </div>
    </article>
  );
}
